import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Globe, TrendingUp, Shield, ChevronDown, ChevronUp, Building2, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const benefits = [
  {
    icon: Globe,
    title: 'Global Exposure',
    description: 'Reach travelers from around the world through our platform.'
  },
  {
    icon: TrendingUp,
    title: 'Business Growth',
    description: 'Increase bookings and revenue with our marketing and distribution.'
  },
  {
    icon: Shield,
    title: 'Secure Platform',
    description: 'Safe and reliable payment processing and booking management.'
  },
  {
    icon: Users,
    title: 'Support Team',
    description: '24/7 partner support to help you succeed on our platform.'
  }
];

const steps = [
  {
    number: '01',
    title: 'Submit Application',
    description: 'Fill out our partnership application form with your business details.'
  },
  {
    number: '02',
    title: 'Verification',
    description: 'Our team reviews your application and verifies your business credentials.'
  },
  {
    number: '03',
    title: 'Onboarding',
    description: 'Complete our onboarding process and set up your partner dashboard.'
  },
  {
    number: '04',
    title: 'Go Live',
    description: 'Start receiving bookings and growing your business with TravelCord.'
  }
];

const Partnership = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    businessType: '',
    services: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'partnership-form',
          ...formData
        }).toString()
      });

      if (response.ok) {
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978"
            alt="Business partnership"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">Partner with TravelCord</h1>
            <p className="text-xl text-white/90 mb-6">
              Join Uganda's leading tourism platform and grow your business with access to global travelers.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Partner With Us</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:border-ta-green/50 transition-all duration-300 hover:shadow-lg group"
              >
                <div className="w-12 h-12 bg-ta-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <benefit.icon className="h-6 w-6 text-ta-green" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-12 text-center">How It Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-4xl font-bold text-ta-green/20 mb-4">{step.number}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 w-full h-0.5 bg-gray-200 transform translate-x-1/2">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-ta-green"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Partnership Opportunities</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Tour Operators</h3>
              <p className="text-gray-600 mb-4">
                List your safari packages, gorilla trekking tours, and other Uganda experiences on our platform to reach a global audience of travelers.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Showcase your tour packages</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Manage bookings efficiently</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Receive direct inquiries</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Accommodation Providers</h3>
              <p className="text-gray-600 mb-4">
                Connect with travelers looking for authentic places to stay during their Uganda safari adventures.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">List your lodges or hotels</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Highlight unique amenities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Manage availability calendar</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Content Partnerships</h3>
              <p className="text-gray-600 mb-4">
                Collaborate on content creation to promote Uganda's tourism offerings and share expertise.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Guest blog opportunities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Cross-promotion</span>
                </li>
                <li className="flex items-start">
                  <span className="text-ta-green mr-2">•</span>
                  <span className="text-gray-600">Expert interviews</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Partner Application</h2>

          <form
            name="partnership-form"
            method="POST"
            data-netlify="true"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <input type="hidden" name="form-name" value="partnership-form" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                <div className="mt-1 relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="companyName"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Name *</label>
                <input
                  type="text"
                  name="contactName"
                  required
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <div className="mt-1 relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder="https://"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Business Type *</label>
                <select
                  name="businessType"
                  required
                  value={formData.businessType}
                  onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                >
                  <option value="">Select type</option>
                  <option value="hotel">Hotel/Accommodation</option>
                  <option value="tour">Tour Operator</option>
                  <option value="transport">Transportation</option>
                  <option value="activity">Activity Provider</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="blog">Travel Blog/Website</option>
                  <option value="influencer">Travel Influencer</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Services Offered *</label>
                <textarea
                  name="services"
                  required
                  value={formData.services}
                  onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder="Describe your services..."
                ></textarea>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Additional Information</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-ta-green focus:ring-ta-green"
                  placeholder="Any additional details you'd like to share..."
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-ta-green text-white px-8 py-3 rounded-lg hover:bg-ta-green-dark transition-colors disabled:opacity-50 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">What Our Partners Say</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80" 
                  alt="Partner" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">David Mugisha</h3>
                  <p className="text-sm text-gray-500">Safari Tour Operator</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "Partnering with TravelCord has significantly increased our bookings for gorilla trekking tours. Their platform is easy to use and their team is always responsive."
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80" 
                  alt="Partner" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">Sarah Namukasa</h3>
                  <p className="text-sm text-gray-500">Lodge Owner</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "Since listing our eco-lodge on TravelCord, we've seen a 40% increase in international guests. The platform has helped us reach travelers we couldn't access before."
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80" 
                  alt="Partner" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <h3 className="font-medium text-gray-900">Joseph Okello</h3>
                  <p className="text-sm text-gray-500">Travel Blogger</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "The content partnership with TravelCord has been mutually beneficial. Their platform gives my Uganda travel content great exposure, and I've gained many new followers."
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Partnership;